import util from '../../lib/util';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from "../find";
import Invoice from '../../entities/invoice';
import Expense from '../../entities/expense';
import FlatFee from '../../entities/flatFee';
import FlatFeeMilestone from '../../entities/flatFeeMilestone';
import InvoiceBatch from '../../entities/invoiceBatch';
import InvoiceBatchItem from '../../entities/invoiceBatchItem';
import Task from '../../entities/task';
import Transaction from '../../entities/transaction';

/*
 * Delete/nullify anywhere this invoice is used in other entities
 *
 * @param {Invoice} invoice
 */
export function deleteInvoice(invoice: Invoice, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = invoice.id;
    }
    /* Simple delete usages */
        
    store.getState().expenses
        .getMappingFor('invoiceId', invoice.id)
        .filter(expenseTemp => expenseTemp.status !== 'D')
        .forEach(expenseTemp => {
            const _expense = expenseTemp.clone();
            _expense.status =  'U';
            _expense.addedOn = newHRTimestamp();
            _expense.addedBy = getCurrentUserId();
            _expense.importSource = targetImportSource;
            _expense.importId = mergeId;

            _expense.invoiceId = null;
            store.dispatch(Actions.ExpenseActions.saveExpenseQueue(_expense));
        });

    store.getState().flatFees
        .getMappingFor('invoiceId', invoice.id)
        .filter(flatFeeTemp => flatFeeTemp.status !== 'D')
        .forEach(flatFeeTemp => {
            const _flatFee = flatFeeTemp.clone();
            _flatFee.status =  'U';
            _flatFee.addedOn = newHRTimestamp();
            _flatFee.addedBy = getCurrentUserId();
            _flatFee.importSource = targetImportSource;
            _flatFee.importId = mergeId;

            _flatFee.invoiceId = null;
            store.dispatch(Actions.FlatFeeActions.saveFlatFeeQueue(_flatFee));
        });

    store.getState().flatFeeMilestones
        .getMappingFor('invoiceId', invoice.id)
        .filter(flatFeeMilestoneTemp => flatFeeMilestoneTemp.status !== 'D')
        .forEach(flatFeeMilestoneTemp => {
            const _flatFeeMilestone = flatFeeMilestoneTemp.clone();
            _flatFeeMilestone.status =  'U';
            _flatFeeMilestone.addedOn = newHRTimestamp();
            _flatFeeMilestone.addedBy = getCurrentUserId();
            _flatFeeMilestone.importSource = targetImportSource;
            _flatFeeMilestone.importId = mergeId;

            _flatFeeMilestone.invoiceId = null;
            store.dispatch(Actions.FlatFeeMilestoneActions.saveFlatFeeMilestoneQueue(_flatFeeMilestone));
        });

    store.getState().invoiceBatches
        .getMappingFor('invoiceId', invoice.id)
        .filter(invoiceBatchTemp => invoiceBatchTemp.status !== 'D')
        .forEach(invoiceBatchTemp => {
            const _invoiceBatch = invoiceBatchTemp.clone();
            _invoiceBatch.status =  'U';
            _invoiceBatch.addedOn = newHRTimestamp();
            _invoiceBatch.importSource = targetImportSource;
            _invoiceBatch.importId = mergeId;

            _invoiceBatch.invoiceIds = _invoiceBatch.invoiceIds?.filter(id => id !== invoice.id) || [];
            store.dispatch(Actions.InvoiceBatchActions.saveInvoiceBatchQueue(_invoiceBatch));
        });

    store.getState().invoiceBatchItems
        .getMappingFor('invoiceId', invoice.id)
        .filter(invoiceBatchItemTemp => invoiceBatchItemTemp.status !== 'D')
        .forEach(invoiceBatchItemTemp => {
            const _invoiceBatchItem = invoiceBatchItemTemp.clone();
            _invoiceBatchItem.status =  'U';
            _invoiceBatchItem.addedOn = newHRTimestamp();
            _invoiceBatchItem.addedBy = getCurrentUserId();
            _invoiceBatchItem.importSource = targetImportSource;
            _invoiceBatchItem.importId = mergeId;

            _invoiceBatchItem.invoiceId = null;
            store.dispatch(Actions.InvoiceBatchItemActions.saveInvoiceBatchItemQueue(_invoiceBatchItem));
        });

    store.getState().tasks
        .getMappingFor('invoiceId', invoice.id)
        .filter(taskTemp => taskTemp.status !== 'D')
        .forEach(taskTemp => {
            const _task = taskTemp.clone();
            _task.status =  'U';
            _task.addedOn = newHRTimestamp();
            _task.addedBy = getCurrentUserId();
            _task.importSource = targetImportSource;
            _task.importId = mergeId;

            _task.invoiceId = null;
            store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
        });

    store.getState().transactions
        .getMappingFor('invoiceId', invoice.id)
        .filter(transactionTemp => transactionTemp.status !== 'D')
        .forEach(transactionTemp => {
            const _transaction = transactionTemp.clone();
            _transaction.status =  'U';
            _transaction.addedOn = newHRTimestamp();
            _transaction.addedBy = getCurrentUserId();
            _transaction.importSource = targetImportSource;
            _transaction.importId = mergeId;

            _transaction.invoiceId = null;
            store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
        });

    /* Remove the actual invoice */
    invoice.status = 'D';
    invoice.addedOn = newHRTimestamp();
    invoice.addedBy = getCurrentUserId();
    invoice.importSource = targetImportSource;
    invoice.importId = mergeId;
    store.dispatch(Actions.InvoiceActions.removeInvoiceQueue(invoice));
    
}

/*
 * Replace anywhere this invoice is used in other entities.
 *
 * @param {Invoice} invoice
 * @param {Invoice} replaceWith
 * @return {Invoice} - Invoice will have refs stripped away
 */
export function replaceInvoice(invoice: Invoice, replaceWith: Invoice, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = invoice.id;
    }
    /* Simple replace usages */
        
    store.getState().expenses
        .getMappingFor('invoiceId', invoice.id)
        .filter(expenseTemp => expenseTemp.status !== 'D')
        .forEach(expenseTemp => {
            const _expense = expenseTemp.clone();
            _expense.status =  'U';
            _expense.addedOn = newHRTimestamp();
            _expense.addedBy = getCurrentUserId();
            _expense.importSource = targetImportSource;
            _expense.importId = mergeId;

            _expense.invoiceId = replaceWith.id;
            store.dispatch(Actions.ExpenseActions.saveExpenseQueue(_expense));
        });

    store.getState().flatFees
        .getMappingFor('invoiceId', invoice.id)
        .filter(flatFeeTemp => flatFeeTemp.status !== 'D')
        .forEach(flatFeeTemp => {
            const _flatFee = flatFeeTemp.clone();
            _flatFee.status =  'U';
            _flatFee.addedOn = newHRTimestamp();
            _flatFee.addedBy = getCurrentUserId();
            _flatFee.importSource = targetImportSource;
            _flatFee.importId = mergeId;

            _flatFee.invoiceId = replaceWith.id;
            store.dispatch(Actions.FlatFeeActions.saveFlatFeeQueue(_flatFee));
        });

    store.getState().flatFeeMilestones
        .getMappingFor('invoiceId', invoice.id)
        .filter(flatFeeMilestoneTemp => flatFeeMilestoneTemp.status !== 'D')
        .forEach(flatFeeMilestoneTemp => {
            const _flatFeeMilestone = flatFeeMilestoneTemp.clone();
            _flatFeeMilestone.status =  'U';
            _flatFeeMilestone.addedOn = newHRTimestamp();
            _flatFeeMilestone.addedBy = getCurrentUserId();
            _flatFeeMilestone.importSource = targetImportSource;
            _flatFeeMilestone.importId = mergeId;

            _flatFeeMilestone.invoiceId = replaceWith.id;
            store.dispatch(Actions.FlatFeeMilestoneActions.saveFlatFeeMilestoneQueue(_flatFeeMilestone));
        });

    store.getState().invoiceBatches
        .getMappingFor('invoiceId', invoice.id)
        .filter(invoiceBatchTemp => invoiceBatchTemp.status !== 'D')
        .forEach(invoiceBatchTemp => {
            const _invoiceBatch = invoiceBatchTemp.clone();
            _invoiceBatch.status =  'U';
            _invoiceBatch.addedOn = newHRTimestamp();
            _invoiceBatch.importSource = targetImportSource;
            _invoiceBatch.importId = mergeId;

            _invoiceBatch.invoiceIds = _invoiceBatch.invoiceIds?.map(id => (id === invoice.id) ? replaceWith.id : id) || [];
            store.dispatch(Actions.InvoiceBatchActions.saveInvoiceBatchQueue(_invoiceBatch));
        });

    store.getState().invoiceBatchItems
        .getMappingFor('invoiceId', invoice.id)
        .filter(invoiceBatchItemTemp => invoiceBatchItemTemp.status !== 'D')
        .forEach(invoiceBatchItemTemp => {
            const _invoiceBatchItem = invoiceBatchItemTemp.clone();
            _invoiceBatchItem.status =  'U';
            _invoiceBatchItem.addedOn = newHRTimestamp();
            _invoiceBatchItem.addedBy = getCurrentUserId();
            _invoiceBatchItem.importSource = targetImportSource;
            _invoiceBatchItem.importId = mergeId;

            _invoiceBatchItem.invoiceId = replaceWith.id;
            store.dispatch(Actions.InvoiceBatchItemActions.saveInvoiceBatchItemQueue(_invoiceBatchItem));
        });

    store.getState().tasks
        .getMappingFor('invoiceId', invoice.id)
        .filter(taskTemp => taskTemp.status !== 'D')
        .forEach(taskTemp => {
            const _task = taskTemp.clone();
            _task.status =  'U';
            _task.addedOn = newHRTimestamp();
            _task.addedBy = getCurrentUserId();
            _task.importSource = targetImportSource;
            _task.importId = mergeId;

            _task.invoiceId = replaceWith.id;
            store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
        });

    store.getState().transactions
        .getMappingFor('invoiceId', invoice.id)
        .filter(transactionTemp => transactionTemp.status !== 'D')
        .forEach(transactionTemp => {
            const _transaction = transactionTemp.clone();
            _transaction.status =  'U';
            _transaction.addedOn = newHRTimestamp();
            _transaction.addedBy = getCurrentUserId();
            _transaction.importSource = targetImportSource;
            _transaction.importId = mergeId;

            _transaction.invoiceId = replaceWith.id;
            store.dispatch(Actions.TransactionActions.saveTransactionQueue(_transaction));
        });

    return invoice;
}

/*
 * Determine the impact of deleting this invoice from other entities
 *
 * @param {Invoice} invoice
 * @return {Array.<{count: number, entity: string}>} - a list of impacted entities, along with a count of impacted entities
 */
export function deleteImpactInvoice(invoice: Invoice) {
    const impacts: string[] = [];
    const state = store.getState();
    
    /* Simple delete usages */
        
    const expenseInvoiceIdImpact = state.expenses
          .getMappingFor('invoiceId', invoice.id)
          .filter(expense => expense.status !== 'D');

    expenseInvoiceIdImpact.forEach(() => impacts.push('expenses'));

    const flatFeeInvoiceIdImpact = state.flatFees
          .getMappingFor('invoiceId', invoice.id)
          .filter(flatFee => flatFee.status !== 'D');

    flatFeeInvoiceIdImpact.forEach(() => impacts.push('flatFees'));

    const flatFeeMilestoneInvoiceIdImpact = state.flatFeeMilestones
          .getMappingFor('invoiceId', invoice.id)
          .filter(flatFeeMilestone => flatFeeMilestone.status !== 'D');

    flatFeeMilestoneInvoiceIdImpact.forEach(() => impacts.push('flatFeeMilestones'));

    const invoiceBatchInvoiceIdsImpact = state.invoiceBatches
          .getMappingFor('invoiceId', invoice.id)
          .filter(invoiceBatch => invoiceBatch.status !== 'D');

    invoiceBatchInvoiceIdsImpact.forEach(() => impacts.push('invoiceBatches'));

    const invoiceBatchItemInvoiceIdImpact = state.invoiceBatchItems
          .getMappingFor('invoiceId', invoice.id)
          .filter(invoiceBatchItem => invoiceBatchItem.status !== 'D');

    invoiceBatchItemInvoiceIdImpact.forEach(() => impacts.push('invoiceBatchItems'));

    const taskInvoiceIdImpact = state.tasks
          .getMappingFor('invoiceId', invoice.id)
          .filter(task => task.status !== 'D');

    taskInvoiceIdImpact.forEach(() => impacts.push('tasks'));

    const transactionInvoiceIdImpact = state.transactions
          .getMappingFor('invoiceId', invoice.id)
          .filter(transaction => transaction.status !== 'D');

    transactionInvoiceIdImpact.forEach(() => impacts.push('transactions'));

    return util.countDistinct(impacts);
}